import { useCallback } from 'react'
import { useMutation, useQueryClient, type QueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { authorizedFetch, parseApiErrorMessage } from '@/lib/api'
import { API_ORIGIN } from '@/constants'
import { SAVED_OPPORTUNITIES_QUERY_KEY } from '@/hooks/use-saved-opportunities'
import {
  opportunityApplicantsQueryKey,
  type ApplicantBoard,
} from '@/hooks/use-opportunity-applicants'
import { jobTalentSummaryQueryKey } from '@/hooks/use-job-talent-summary'
import { useFitRetryPending } from '@/hooks/use-fit-retry-pending'

type CandidateRetryVars = { kind: 'candidate'; opportunityId: string }
type EmployerRetryVars = { kind: 'employer'; applicationId: string; opportunityId: string }
type RetryVars = CandidateRetryVars | EmployerRetryVars

type CandidateListVars = { kind: 'candidate-list'; opportunityIds: string[] }
type EmployerListVars = {
  kind: 'employer-list'
  opportunityId: string
  applicationIds: string[]
}
type ListRetryVars = CandidateListVars | EmployerListVars

async function postRetry(path: string): Promise<void> {
  const response = await authorizedFetch(`${API_ORIGIN}${path}`, { method: 'POST' })
  if (response.ok) return
  throw new Error(await parseApiErrorMessage(response, 'Could not start analysis again'))
}

async function postRetryQuiet(path: string): Promise<'ok' | 'cooldown'> {
  const response = await authorizedFetch(`${API_ORIGIN}${path}`, { method: 'POST' })
  if (response.ok) return 'ok'
  if (response.status === 429) return 'cooldown'
  throw new Error(await parseApiErrorMessage(response, 'Could not start analysis again'))
}

function toastListRetryResult(tone: 'candidate' | 'employer', queued: number, errorMessage?: string) {
  if (queued > 0) {
    toast.success(
      tone === 'candidate'
        ? 'Checking fit for your saved jobs…'
        : 'Scoring your applicants…',
    )
    return
  }
  if (errorMessage) {
    toast.error(errorMessage)
    return
  }
  toast.error('Wait a bit before running again.')
}

function clearEmployerMatchScores(
  queryClient: QueryClient,
  opportunityId: string,
  applicationIds: string[],
) {
  const ids = new Set(applicationIds)
  queryClient.setQueryData<ApplicantBoard>(
    opportunityApplicantsQueryKey(opportunityId),
    (prev) => {
      if (!prev) return prev
      return {
        ...prev,
        applicants: prev.applicants.map((row) =>
          ids.has(row.id) ? { ...row, match_score: null } : row,
        ),
      }
    },
  )
}

export function useRetryFitAnalysis() {
  const queryClient = useQueryClient()
  const pending = useFitRetryPending()

  const mutation = useMutation({
    mutationFn: async (vars: RetryVars) => {
      if (vars.kind === 'candidate') {
        await postRetry(
          `/api/opportunities/saved/${encodeURIComponent(vars.opportunityId)}/retry-analysis/`,
        )
        return
      }
      await postRetry(
        `/api/applications/${encodeURIComponent(vars.applicationId)}/retry-analysis/`,
      )
    },
    onSuccess: (_data, vars) => {
      if (vars.kind === 'candidate') {
        pending.markOpportunityPending(vars.opportunityId)
        void queryClient.invalidateQueries({ queryKey: SAVED_OPPORTUNITIES_QUERY_KEY })
        void queryClient.invalidateQueries({ queryKey: ['evaluation-analysis'] })
        return
      }
      pending.markApplicationPending(vars.applicationId)
      clearEmployerMatchScores(queryClient, vars.opportunityId, [vars.applicationId])
      void queryClient.invalidateQueries({
        queryKey: opportunityApplicantsQueryKey(vars.opportunityId),
      })
      void queryClient.invalidateQueries({
        queryKey: jobTalentSummaryQueryKey(vars.opportunityId),
      })
      void queryClient.invalidateQueries({ queryKey: ['evaluation-analysis'] })
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Could not start analysis again')
    },
  })

  const listMutation = useMutation({
    mutationFn: async (vars: ListRetryVars) => {
      const queuedIds: string[] = []
      let skippedCooldown = 0
      let lastError: string | undefined

      if (vars.kind === 'candidate-list') {
        const ids = vars.opportunityIds.filter((id) => !pending.pendingOpportunityIds.has(id))
        for (const opportunityId of ids) {
          try {
            const result = await postRetryQuiet(
              `/api/opportunities/saved/${encodeURIComponent(opportunityId)}/retry-analysis/`,
            )
            if (result === 'ok') {
              pending.markOpportunityPending(opportunityId)
              queuedIds.push(opportunityId)
            } else {
              skippedCooldown += 1
            }
          } catch (error) {
            lastError = error instanceof Error ? error.message : 'Could not start analysis again'
          }
        }
        return {
          tone: 'candidate' as const,
          queued: queuedIds.length,
          skippedCooldown,
          lastError,
        }
      }

      const ids = vars.applicationIds.filter((id) => !pending.pendingApplicationIds.has(id))
      for (const applicationId of ids) {
        try {
          const result = await postRetryQuiet(
            `/api/applications/${encodeURIComponent(applicationId)}/retry-analysis/`,
          )
          if (result === 'ok') {
            pending.markApplicationPending(applicationId)
            queuedIds.push(applicationId)
          } else {
            skippedCooldown += 1
          }
        } catch (error) {
          lastError = error instanceof Error ? error.message : 'Could not start analysis again'
        }
      }
      return {
        tone: 'employer' as const,
        opportunityId: vars.opportunityId,
        queuedIds,
        queued: queuedIds.length,
        skippedCooldown,
        lastError,
      }
    },
    onSuccess: (data) => {
      if (data.tone === 'candidate') {
        void queryClient.invalidateQueries({ queryKey: SAVED_OPPORTUNITIES_QUERY_KEY })
        void queryClient.invalidateQueries({ queryKey: ['evaluation-analysis'] })
      } else {
        if (data.queuedIds.length > 0) {
          clearEmployerMatchScores(queryClient, data.opportunityId, data.queuedIds)
        }
        void queryClient.invalidateQueries({
          queryKey: opportunityApplicantsQueryKey(data.opportunityId),
        })
        void queryClient.invalidateQueries({
          queryKey: jobTalentSummaryQueryKey(data.opportunityId),
        })
        void queryClient.invalidateQueries({ queryKey: ['evaluation-analysis'] })
      }

      const nothingToStart = data.queued === 0 && data.skippedCooldown === 0 && !data.lastError
      if (nothingToStart) return
      toastListRetryResult(data.tone, data.queued, data.lastError)
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Could not start analysis again')
    },
  })

  const mutatingOpportunityId =
    mutation.isPending && mutation.variables?.kind === 'candidate'
      ? mutation.variables.opportunityId
      : null
  const mutatingApplicationId =
    mutation.isPending && mutation.variables?.kind === 'employer'
      ? mutation.variables.applicationId
      : null

  const retryCandidate = useCallback(
    (opportunityId: string) => mutation.mutate({ kind: 'candidate', opportunityId }),
    [mutation],
  )
  const retryEmployer = useCallback(
    (applicationId: string, opportunityId: string) =>
      mutation.mutate({ kind: 'employer', applicationId, opportunityId }),
    [mutation],
  )
  const retryCandidateList = useCallback(
    (opportunityIds: string[]) => {
      if (opportunityIds.length === 0) return
      listMutation.mutate({ kind: 'candidate-list', opportunityIds })
    },
    [listMutation],
  )
  const retryEmployerList = useCallback(
    (opportunityId: string, applicationIds: string[]) => {
      if (applicationIds.length === 0) return
      listMutation.mutate({ kind: 'employer-list', opportunityId, applicationIds })
    },
    [listMutation],
  )

  return {
    retryCandidate,
    retryEmployer,
    retryCandidateList,
    retryEmployerList,
    mutatingOpportunityId,
    mutatingApplicationId,
    isPending: mutation.isPending,
    isListPending: listMutation.isPending,
    pendingApplicationIds: pending.pendingApplicationIds,
    stuckApplicationIds: pending.stuckApplicationIds,
  }
}
